import { all_routes as routes } from "../feature-module/router/all_routes";

export const isTeacher = (user: any): boolean => {
  return user?.role?.name?.toLowerCase() === 'teacher';
};

export const isStudent = (user: any): boolean => {
  return user?.role?.name?.toLowerCase() === 'student';
};

// Super-admin = owner or admin role. Used to gate management screens that must
// never be exposed to other roles even if a permission is granted.
export const isSuperAdmin = (user: any): boolean =>
  ['owner', 'admin'].includes(user?.role?.name?.toLowerCase());


// Owner only -- narrower than super-admin. Gates the Activity Log, which the
// backend likewise serves to the owner role alone.
export const isOwner = (user: any): boolean =>
  user?.role?.name?.toLowerCase() === 'owner';

// Activity Log access. Decided by the backend (owner role, narrowed to the
// accounts named in its ACTIVITY_LOG_VIEWER_IDS) and sent on login/profile.
export const canViewActivityLog = (user: any): boolean =>
  user?.canViewActivityLog === true;

export const canAccess = (requiredPermission: string, user: any): boolean => {
  if (!user || !user.permissions) return false;
  
  if (user.permissions.length === 0) {
    return true;
  }
  
  return user.permissions.includes(requiredPermission);
};

// Where to send a user after login. Every user was previously forced to the
// admin dashboard, which needs "dashboard.view" — so any role without it (e.g.
// cashier) bounced to /unauthorized. Instead, land them on the first page they
// actually have access to, in a sensible priority order. Falls back to My
// Profile (needs no permission) so no one ever lands on /unauthorized.
export const getLandingRoute = (user: any): string => {
  const candidates: { path: string; permission?: string }[] = [
    { path: routes.adminDashboard, permission: "dashboard.view" },
    { path: routes.collectFees, permission: "fees.view" },
    { path: routes.studentList, permission: "student.view" },
    { path: routes.admissionFormList, permission: "admission.view" },
    { path: routes.registrationFormList, permission: "registration.view" },
    { path: routes.reports, permission: "reports.view" },
    { path: routes.contractors, permission: "transport.view" },
    { path: routes.schoolOpening, permission: "school-opening.view" },
    { path: routes.profile }, // always accessible fallback
  ];

  for (const c of candidates) {
    if (!c.permission) return c.path;
    if (canAccess(c.permission, user)) return c.path;
  }
  return routes.profile;
};

export const flattenPermissions = (rolePermissions: any[]): string[] => {
  if (!rolePermissions || rolePermissions.length === 0) {
    return [];
  }
  
  const permissions: string[] = [];
  
  rolePermissions.forEach((perm: any) => {
    const moduleName = perm.module.name;
    perm.grantedActions.forEach((action: string) => {
      permissions.push(`${moduleName}.${action}`);
    });
  });
  
  return permissions;
};